import { Component, Output, EventEmitter, signal, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

export interface GiphyGif {
  id: string;
  title: string;
  preview: { url: string; width: number; height: number };
  original: { url: string; width: number; height: number };
}

@Component({
  selector: 'app-gif-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gif-picker.html',
  styleUrl: './gif-picker.css'
})
export class GifPicker implements OnDestroy {
  @Output() gifSelected = new EventEmitter<GiphyGif>();
  @Output() closed = new EventEmitter<void>();

  private http = inject(HttpClient);
  private destroy$ = new Subject<void>();
  private gifSearch$ = new Subject<string>();

  searchQuery = signal('');
  results = signal<GiphyGif[]>([]);
  loading = signal(false);

  constructor() {
    this.gifSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query.trim()) {
          return this.http.get<{ gifs: GiphyGif[] }>('/api/giphy/trending');
        }
        return this.http.get<{ gifs: GiphyGif[] }>('/api/giphy/search', {
          params: { q: query }
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res) => {
        this.results.set(res.gifs);
        this.loading.set(false);
      },
      error: () => {
        this.results.set([]);
        this.loading.set(false);
      }
    });

    // Load trending on init
    this.loading.set(true);
    this.gifSearch$.next('');
  }

  onSearch(query: string): void {
    this.searchQuery.set(query);
    this.loading.set(true);
    this.gifSearch$.next(query);
  }

  selectGif(gif: GiphyGif): void {
    this.gifSelected.emit(gif);
  }

  close(): void {
    this.closed.emit();
  }

  trackByGif(index: number, gif: GiphyGif): string {
    return gif.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
